/// <reference path="../../styled.d.ts" />

import * as React from 'react';
import styled from 'styled-components/native';
import { Theme, theme } from '../../theme';
import { BoxesIcon } from '../../icons/Boxes';
import { CalendarIcon } from '../../icons/Calendar';
import { CloudIcon } from '../../icons/Cloud';
import { CustomerIcon } from '../../icons/Customer';
import { DeficiencyIcon } from '../../icons/Deficiency';
import { EquipmentIcon } from '../../icons/Equipment';
import { FieldIcon } from '../../icons/Field';
import { FloorPlanIcon } from '../../icons/FloorPlan';
import { InfoIcon } from '../../icons/Info';
import { LocationIcon } from '../../icons/Location';
import { MeterIcon } from '../../icons/Meter';
import { ProfileIcon } from '../../icons/Profile';
import { QuestionIcon } from '../../icons/Question';
import { SearchIcon } from '../../icons/Search';
import { SelectedFieldIcon } from '../../icons/SelectedField';
import { WarningIcon } from '../../icons/Warning';
import { WrenchIcon } from '../../icons/Wrench';
import { Text } from '../Text';
import { Translate } from '../Translate';

export interface ListSectionTitleProps {
  children: React.ReactNode;
  icon?: React.ReactNode;
}

const ListSectionTitleView = styled.View`
  align-items: center;
  justify-content: flex-start;
  flex-direction: row;
  border-bottom-color: ${(props: { theme: Theme }) => props.theme.palette.lineColor};
  border-bottom-width: 1px;
  padding: ${(props) => props.theme.spacing.md};
  padding-top: ${(props) => props.theme.spacing.md};
  padding-bottom: ${(props) => props.theme.spacing.md};
  gap: ${(props) => props.theme.spacing.md};
`;

const ListSectionTitleIcon = styled.View`
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${(props) => props.theme.spacing.xxl};
  height: ${(props) => props.theme.spacing.xxl};
  border-radius: ${(props) => props.theme.spacing.xxl};
  background-color: ${(props: { theme: Theme }) => props.theme.palette.searchBarColor};
`;

const getTitleText = (children: React.ReactNode): string => {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }

  if (Array.isArray(children)) {
    return children.map(getTitleText).join(' ');
  }

  if (React.isValidElement(children)) {
    return getTitleText(children.props.children);
  }

  return '';
}

const getDefaultIcon = (children: React.ReactNode) => {
  const title = getTitleText(children).trim().toLowerCase();

  if (!title) {
    return <InfoIcon />;
  }

  if (title.includes('custom field')) {
    return <FieldIcon />;
  }

  if (title.includes('option') || title.includes('question')) {
    return <QuestionIcon />;
  }

  if (title.includes('care')) {
    return <WrenchIcon />;
  }

  if (title.includes('deficien')) {
    return <DeficiencyIcon />;
  }

  if (title.includes('address')) {
    return <LocationIcon />;
  }

  if (title.includes('contact') || title.includes('permission')) {
    return <ProfileIcon />;
  }

  if (title.includes('customer')) {
    return <CustomerIcon />;
  }

  if (title.includes('model')) {
    return <EquipmentIcon />;
  }

  if (title.includes('billing')) {
    return <MeterIcon />;
  }

  if (title.includes('search')) {
    return <SearchIcon />;
  }

  if (title.includes('data')) {
    return <CloudIcon />;
  }

  if (title.includes('service') || title.includes('calendar')) {
    return <CalendarIcon />;
  }

  if (title.includes('disabled')) {
    return <WarningIcon />;
  }

  if (title.includes('company')) {
    return <FloorPlanIcon />;
  }

  if (title.includes('setup')) {
    return <BoxesIcon />;
  }

  if (title.includes('white label')) {
    return <SelectedFieldIcon />;
  }

  return <InfoIcon />;
}

const translateChildren = (children: React.ReactNode): React.ReactNode => {
  if (typeof children === 'string' || typeof children === 'number') {
    return <Translate text={String(children)} />;
  }

  if (Array.isArray(children)) {
    return children.map((child, idx) => (
      <React.Fragment key={idx}>{translateChildren(child)}</React.Fragment>
    ));
  }

  return children;
}

export const ListSectionTitle = (props: ListSectionTitleProps) => {
  const icon = props.icon || getDefaultIcon(props.children);

  return (
    <ListSectionTitleView>
      {icon && (
        <ListSectionTitleIcon>
          {React.isValidElement<{ color?: string }>(icon)
            ? React.cloneElement(icon, { color: theme.palette.fadedText })
            : icon
          }
        </ListSectionTitleIcon>
      )}

      <Text size="md" weight="medium">{translateChildren(props.children)}</Text>
    </ListSectionTitleView>
  )
}
